import type {
  ClaimEvidence,
  DeckIntelligence,
  DeckIntelligenceCompetitor,
  DeckIntelligenceSectionScores,
} from '../types';

export const SECTION_SCORE_KEYS = [
  { key: 'team' as const, label: 'Team' },
  { key: 'market' as const, label: 'Market' },
  { key: 'product' as const, label: 'Product' },
  { key: 'businessModel' as const, label: 'Business model' },
  { key: 'traction' as const, label: 'Traction' },
  { key: 'funding' as const, label: 'Funding' },
];

const EVIDENCE_SCORES: Record<ClaimEvidence, number> = {
  supported: 10,
  weak: 6,
  speculative: 3,
  likely_false: 0,
};

export type ScoreBand = 'strong' | 'mixed' | 'weak';

/** For metrics like risk or market pressure, a high value is bad, not good. */
export type ScoreDirection = 'higherIsBetter' | 'higherIsWorse';

export function scoreBand(
  value: number,
  max = 10,
  direction: ScoreDirection = 'higherIsBetter'
): ScoreBand {
  const ratio = max === 0 ? 0 : value / max;
  const goodness = direction === 'higherIsWorse' ? 1 - ratio : ratio;
  if (goodness >= 0.7) return 'strong';
  if (goodness >= 0.45) return 'mixed';
  return 'weak';
}

export function scoreBandLabel(
  band: ScoreBand,
  direction: ScoreDirection = 'higherIsBetter'
): string {
  if (direction === 'higherIsWorse') {
    if (band === 'strong') return 'Low';
    if (band === 'mixed') return 'Elevated';
    return 'High';
  }
  if (band === 'strong') return 'Strong';
  if (band === 'mixed') return 'Mixed';
  return 'Weak';
}

export function parseRiskLevel(text: string): number {
  const normalized = text.trim().toLowerCase();

  if (/\b(critical|severe)\b/.test(normalized)) return 10;
  if (/\bhigh\b/.test(normalized)) return 8;
  if (/\b(medium|moderate)\b/.test(normalized)) return 5;
  if (/\blow\b/.test(normalized)) return 2;

  return 5;
}

export function claimCredibility(intelligence: Pick<DeckIntelligence, 'claims'>): {
  average: number;
  bullshitRatio: number;
  counts: Record<ClaimEvidence, number>;
} {
  const counts: Record<ClaimEvidence, number> = {
    supported: 0,
    weak: 0,
    speculative: 0,
    likely_false: 0,
  };

  for (const claim of intelligence.claims) {
    counts[claim.evidenceEvaluation] += 1;
  }

  if (intelligence.claims.length === 0) {
    return { average: 0, bullshitRatio: 0, counts };
  }

  const total = intelligence.claims.reduce(
    (sum, claim) => sum + EVIDENCE_SCORES[claim.evidenceEvaluation],
    0
  );
  const average = total / intelligence.claims.length;
  const bullshitRatio =
    (counts.speculative + counts.likely_false * 1.5) / intelligence.claims.length;

  return { average, bullshitRatio, counts };
}

export function aggregateRisk(intelligence: Pick<DeckIntelligence, 'risks'>): {
  average: number;
  peak: number;
} {
  if (intelligence.risks.length === 0) {
    return { average: 0, peak: 0 };
  }

  const levels = intelligence.risks.map((risk) => parseRiskLevel(risk.riskEvaluation));
  const total = levels.reduce((sum, level) => sum + level, 0);

  return {
    average: total / levels.length,
    peak: Math.max(...levels),
  };
}

export function sectionPotential(sectionScores?: DeckIntelligenceSectionScores): {
  total: number;
  max: number;
  average: number;
} | null {
  if (!sectionScores) return null;

  const values = SECTION_SCORE_KEYS.map(({ key }) => sectionScores[key]);
  const total = values.reduce((sum, value) => sum + value, 0);

  return {
    total,
    max: values.length * 10,
    average: total / values.length,
  };
}

export function marketCrowding(competitors?: DeckIntelligenceCompetitor[]): {
  totalStrength: number;
  maxStrength: number;
  averageStrength: number;
  count: number;
} {
  if (!competitors?.length) {
    return { totalStrength: 0, maxStrength: 0, averageStrength: 0, count: 0 };
  }

  const totalStrength = competitors.reduce((sum, competitor) => sum + competitor.marketStrength, 0);

  return {
    totalStrength,
    maxStrength: competitors.length * 10,
    averageStrength: totalStrength / competitors.length,
    count: competitors.length,
  };
}

export function synthesisBalance(intelligence: Pick<DeckIntelligence, 'analysis'>): {
  upsideCount: number;
  downsideCount: number;
  balance: number;
} {
  const upsideCount = intelligence.analysis.top3UnicornReasons.filter((item) => item.trim()).length;
  const downsideCount = intelligence.analysis.top3DeathReasons.filter((item) => item.trim()).length;
  const total = upsideCount + downsideCount;
  const balance = total === 0 ? 0.5 : upsideCount / total;

  return { upsideCount, downsideCount, balance };
}

export function overallOpportunityScore(intelligence: DeckIntelligence): {
  score: number;
  max: number;
  components: Array<{ label: string; value: number; max: number }>;
} {
  const potential = sectionPotential(intelligence.sectionScores);
  const credibility = claimCredibility(intelligence);
  const risk = aggregateRisk(intelligence);
  const crowding = marketCrowding(intelligence.competitors);

  const components: Array<{ label: string; value: number; max: number }> = [];

  if (potential) {
    components.push({ label: 'Potential', value: potential.average, max: 10 });
  }

  if (intelligence.claims.length > 0) {
    components.push({ label: 'Credibility', value: credibility.average, max: 10 });
  }

  if (intelligence.risks.length > 0) {
    components.push({ label: 'Safety', value: 10 - risk.average, max: 10 });
  }

  if (crowding.count > 0) {
    components.push({ label: 'White space', value: 10 - crowding.averageStrength, max: 10 });
  }

  const balance = synthesisBalance(intelligence);
  if (balance.upsideCount + balance.downsideCount > 0) {
    components.push({ label: 'Upside tilt', value: balance.balance * 10, max: 10 });
  }

  if (components.length === 0) {
    return { score: 0, max: 10, components };
  }

  const score =
    components.reduce((sum, component) => sum + component.value, 0) / components.length;

  return { score, max: 10, components };
}

export function competitorFaviconUrl(websiteUrl?: string): string | undefined {
  if (!websiteUrl?.trim()) return undefined;

  try {
    const hostname = new URL(websiteUrl.trim()).hostname.replace(/^www\./, '');
    if (!hostname) return undefined;
    return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(hostname)}&sz=32`;
  } catch {
    return undefined;
  }
}

export function formatScore(value: number, max = 10): string {
  return `${value.toFixed(max === 10 && Number.isInteger(value) ? 0 : 1)}/${max}`;
}
